import { useDebounceFn, useMount, useRequest, useKeyPress } from 'ahooks'
import classNames from 'classnames/bind'
import React, { useEffect, useRef, useState, useMemo } from 'react'
import { updateLike } from 'servers/recommend'
import store from 'store2'
import { encrypt, getAppVersionCompatibility } from '@docs/common'
import useLanguageTrans from 'common/useLanguageTrans'
import Share from 'pages/common/Share'
import styles from './styles.module.scss'
import { trackUpdateLikeSolution, trackFeedbackSolution, trackEnterSolutionPage, trackUnLikeSolution } from '../../../common/track'
import { useTranslation } from '@volcengine/i18n'

const cn = classNames.bind(styles)

type Props = {
  size?: 'small' | 'normal'
  type: 'recommend' | 'solution'
  id: string
  uid: string
  likeCount: number
  isLike: boolean
  shareTitle: string
  shareContent?: string
  feedbackPlaceholder?: string
}

const ThrottleClickWait = 5000 // 5s防抖点击
const StoreKeyPrefix = 'recommend_like'



const SHARE_CONTENT = "Get home security advice and practical tips from eufy security's Tips and Hints section!"

// 安卓 点击文本框 会被键盘遮挡
const useKeyboardHeight = () => {
  const [keyboardHeight, setKeyboardHeight] = useState(0)

  useEffect(() => {
    const updateKeyboardHeight = () => {
      if (window.innerHeight < document.documentElement.clientHeight) {
        setKeyboardHeight(document.documentElement.clientHeight - window.innerHeight)
      } else {
        setKeyboardHeight(0)
      }
    }

    window.addEventListener('resize', updateKeyboardHeight)

    return () => {
      window.removeEventListener('resize', updateKeyboardHeight)
    }
  }, [])

  return keyboardHeight
}

const Footer: React.FC<Props> = ({
  size = 'normal',
  isLike,
  id,
  uid,
  type,
  likeCount,
  shareTitle,
  shareContent = SHARE_CONTENT,
  feedbackPlaceholder = '',
}) => {
  const storeKey = `${StoreKeyPrefix}_${id}`
  const [count, setCount] = useState(likeCount)

  const [like, setLike] = useState(isLike)
  const [feedbackVisible, setFeedbackVisible] = useState(false)
  const [shareVisible, setShareVisible] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [submited, setSubmited] = useState(false)

  const feedbackInput = useRef<HTMLTextAreaElement>(null)
  const keyboardHeight = useKeyboardHeight()
  // 多语言
  useLanguageTrans()
  const { t } = useTranslation()

  // 获取 app 参数
  const appParam = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('app')
  }, [])

  // soundcore 暂不支持分享功能
  const shouldShowShare = useMemo(() => {
    return type === 'solution' && appParam !== 'soundcore'
  }, [type, appParam])

  // 监听enter键
  useKeyPress('enter', () => {
    if (!feedback) return
    setFeedbackVisible(false)
    setSubmited(true)
    setFeedback('')
    feedbackInput.current?.blur()
    trackEnterSolutionPage(String(id), JSON.stringify({ content: feedback }))

    const t = setTimeout(() => {
      setSubmited(false)
      clearTimeout(t)
    }, 1500)
  })

  // 更新like状态 点赞/取消赞
  const { run: runUpdateLike, runAsync: runUpdateLikeAsync } = useRequest(
    async (_like: boolean) =>
      updateLike({
        is_like: _like ?? like!,
        article_id:
          (await getAppVersionCompatibility()) === 'v3'
            ? String(uid)
            : await encrypt({
                data: uid,
                type: 0,
              }),
        type,
      }),
    {
      manual: true,
      onSuccess(res, [_like]) {
        trackUpdateLikeSolution(
          id,
          type === 'recommend' ? 'RecommendArticle' : 'SolutionArticle',
          _like ? 'like' : 'dislike'
        )

        store.local.remove(storeKey)
      },
    }
  )

  const { run: runDebounceReq } = useDebounceFn(runUpdateLike, {
    wait: ThrottleClickWait,
    trailing: true,
    leading: false,
  })

  //事件上报
  const toggleLike = () => {
    if (!like) {
      trackUnLikeSolution(
        String(id),
        type === 'recommend' ? 'RecommendArticle' : 'SolutionArticle',
        'dislike'
      )
    } else {
      trackUpdateLikeSolution(
        String(id),
        type === 'recommend' ? 'RecommendArticle' : 'SolutionArticle',
        'like'
      )
    }

    setLike(prev => !prev)
    setCount(prevCount => {
      const _count = !like ? prevCount + 1 : prevCount - 1
      return _count
    })
    // 把like状态存缓存中 防止太快切走页面导致没请求服务 等回到页面再调用接口
    store.local.set(storeKey, !like)

    runDebounceReq(!like)
  }

  // 点击文本框
  const openFeedback = () => {
    trackFeedbackSolution(String(id), type === 'recommend' ? 'RecommendArticle' : 'SolutionArticle', 'feedback')
    setFeedbackVisible(true)
  }

  useMount(() => {
    const _like = store.local.get(storeKey)
    // 进入时取缓存的like状态进行更新
    if (_like !== null && _like !== undefined) {
      runUpdateLikeAsync(_like).then(res => {
        if (res.code === 0 || res.code === 200) {
          setLike(_like)
        }
      })
    }
  })

  useEffect(() => {
    setLike(isLike)
  }, [isLike])

  useEffect(() => {
    setCount(likeCount)
  }, [likeCount])

  const handleFocus = () => {
    // 安卓设备可能需要延迟以等待软键盘弹出
    setTimeout(() => {
      if (feedbackInput.current) {
        feedbackInput.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
      }
    }, 300) // 延迟时间可能需要根据实际情况调整
  }

  const handleBlur = () => {
    // 键盘关闭时重置
    window.scrollTo(0, 0)
  }
  useEffect(() => {
    const inputElement = feedbackInput.current
    if (feedbackVisible) {
      feedbackInput.current?.focus()
      inputElement?.addEventListener('focus', handleFocus)
      inputElement?.addEventListener('blur', handleBlur)
    }
    return () => {
      inputElement?.removeEventListener('focus', handleFocus)
      inputElement?.removeEventListener('blur', handleBlur)
    }
  }, [feedbackVisible])



  return (
    <>
      <div className={cn(styles.footerWrap, { small: size === 'small' })}>
        {/* 点赞icon */}
        <div className={styles.iconBox}>
          <div
            className={cn({
              icon: true,
              active: like,
            })}
            onClick={toggleLike}
          >
            {count ? <span className={styles.likeCount}>{count}</span> : null}
          </div>
        </div>
        <i></i>
        {/* 文本框icon */}
        <div className={styles.iconBox}>
          <div className={styles.icon} onClick={openFeedback}></div>
        </div>
        {/* 分享icon */}
        {shouldShowShare && (
          <>
            <i></i>
            <div className={styles.iconBox}>
              <div className={styles.icon} onClick={() => setShareVisible(true)}></div>
            </div>
          </>
        )}
      </div>
      {/* 文本框弹框 */}
      <div className={styles.feedback} style={{ display: feedbackVisible ? '' : 'none' }}>
        <div className={styles.feedbackMask} onClick={() => setFeedbackVisible(false)}></div>
        <div className={styles.feedbackBox} style={{ bottom: keyboardHeight }}>
          <textarea
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            ref={feedbackInput}
            placeholder={feedbackPlaceholder}
            autoFocus
          />
        </div>
      </div>
      {/* 文本框输入提交 */}
      <div className={styles.submited} style={{ display: submited ? '' : 'none' }}>
        <i></i>
        <span>{t('rec_submit')}</span>
      </div>
      {/* 分享弹框 */}
      {shouldShowShare && (
        <Share
          title={shareTitle}
          content={shareContent || SHARE_CONTENT}
          link={document.URL}
          shares={[
            { method: 'email' },
            { method: 'facebook' },
            { method: 'twitter' },
            { method: 'messenger' },
            { method: 'copylink' },
          ]}
          visible={shareVisible}
          setVisible={setShareVisible}
        />
      )}
    </>
  )
}

export default Footer
